import { useMemo } from 'react';

import { formatCurrency } from '../blocos/tipos';
import {
  janelaAnterior,
  nomeDoPeriodo,
  rotuloDaJanela,
  serieTicketMensal,
  ticketDoPeriodo,
  type Janela,
  type Periodo,
  type VendasPorMes,
} from './periodo';
import { SeletorPeriodo } from './SeletorPeriodo';
import type { DadosInicio } from './useDadosInicio';
import { CardBento, Rotulo, Sub, ValorGrande } from './ui';
import { useMascara } from '../../valores-ocultos/contexto';

/**
 * Os contadores pequenos do bento — um número, uma linha de apoio, um clique.
 *
 * Valores em BRL sempre POR EXTENSO (formatCurrency compact): a notação
 * "R$ 1 mi" arredondava 1.300.000 pra baixo e parecia produção faltando — a
 * regra está documentada em blocos/tipos.ts e vale pra cá também.
 */

/** Um mês sem venda não tem ticket — e ticket não é média de médias. */
const ticketDoMes = (mes: { valor: number; count: number } | undefined) =>
  mes && mes.count > 0 ? mes.valor / mes.count : null;

/**
 * A linhazinha dourada do mock, com dado real.
 *
 * Mês sem venda NÃO vira zero: ticket zero afirmaria contratos de R$ 0 que
 * nunca existiram, e o mergulho ao chão dominaria a escala dos meses que de
 * fato venderam. Buraco é buraco — mas partida em pedaços soltos a linha
 * parecia gráfico quebrado, então o vazio é atravessado por um TRACEJADO
 * claro, que liga os meses com venda sem afirmar valor nenhum no meio. Um mês
 * sozinho entre dois vazios vira um ponto.
 *
 * O desenho é esticado na largura do card (viewBox 120×30 em ~500px), e o
 * esticão deformava tudo: o traço engrossava na subida e afinava no plano, e
 * o ponto virava elipse. Por isso o traço não escala (`non-scaling-stroke`) e
 * os pontos ficam fora do viewBox, posicionados em %.
 */
const Sparkline = ({ serie }: { serie: (number | null)[] }) => {
  const trechos = useMemo(() => {
    const valores = serie.filter((v): v is number => v != null);
    // Um ponto só não desenha tendência nenhuma.
    if (valores.length < 2) return [];
    const max = Math.max(...valores);
    const min = Math.min(...valores);
    const range = Math.max(1, max - min);
    const segmentos: { x: number; y: number }[][] = [];
    let atual: { x: number; y: number }[] = [];
    serie.forEach((v, i) => {
      if (v == null) {
        if (atual.length) segmentos.push(atual);
        atual = [];
        return;
      }
      atual.push({
        x: serie.length > 1 ? (i * 120) / (serie.length - 1) : 60,
        y: 26 - ((v - min) * 22) / range,
      });
    });
    if (atual.length) segmentos.push(atual);
    return segmentos;
  }, [serie]);

  if (!trechos.length) return null;

  const traco = {
    fill: 'none',
    stroke: '#BEB692',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    vectorEffect: 'non-scaling-stroke',
  } as const;

  return (
    <svg className="mt-3 block h-[30px] w-full overflow-visible">
      <svg viewBox="0 0 120 30" preserveAspectRatio="none" width="100%" height="100%" overflow="visible">
        {trechos.slice(1).map((trecho, i) => {
          const de = trechos[i][trechos[i].length - 1];
          const ate = trecho[0];
          return (
            <line
              key={`vazio-${i}`}
              data-vazio=""
              x1={de.x}
              y1={de.y}
              x2={ate.x}
              y2={ate.y}
              {...traco}
              strokeWidth={1.5}
              strokeDasharray="3 4"
              opacity={0.55}
            />
          );
        })}
        {trechos.map((trecho, i) =>
          trecho.length > 1 ? (
            <polyline
              key={i}
              points={trecho.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
              {...traco}
            />
          ) : null,
        )}
      </svg>
      {trechos.map((trecho, i) =>
        trecho.length === 1 ? (
          <circle
            key={i}
            cx={`${((trecho[0].x / 120) * 100).toFixed(2)}%`}
            cy={trecho[0].y}
            r="2.5"
            fill="#BEB692"
          />
        ) : null,
      )}
    </svg>
  );
};

/**
 * Ticket médio do período — total vendido ÷ cotas, NUNCA a média dos tickets
 * mensais: um mês com uma cota grande pesaria igual a um mês com trinta, e a
 * média mentiria para cima.
 *
 * O período vem de fora e é o mesmo do card de produção (ver CardProducaoMes):
 * clicar aqui troca os dois. A comparação acompanha: mês contra o mês anterior,
 * semestre contra o semestre anterior, ano contra o ano anterior — e some
 * quando não há período anterior com venda, porque "+100%" contra o nada é
 * ruído.
 */
export const CardTicketMedio = ({
  data,
  periodo,
  janela,
  onPeriodoChange,
}: {
  data: DadosInicio;
  periodo: Periodo;
  janela: Janela;
  onPeriodoChange: (periodo: Periodo) => void;
}) => {
  const m = useMascara();
  const anterior = useMemo(() => janelaAnterior(periodo), [periodo]);
  const ticket = ticketDoPeriodo(data.vendasPorMes, janela);
  const ticketAnterior = ticketDoPeriodo(data.vendasPorMes, anterior);
  const varPct =
    ticket != null && ticketAnterior != null && ticketAnterior > 0
      ? ((ticket - ticketAnterior) * 100) / ticketAnterior
      : null;

  // No mês, a linhazinha continua olhando os 12 meses rolantes do gráfico
  // grande — é o histórico que dá contexto a um número de 23 dias. No semestre
  // e no ano, ela mostra os meses DO período (os futuros sem ponto).
  const serie = useMemo(() => {
    if (periodo !== 'mes') {
      return serieTicketMensal(data.vendasPorMes, janela).map((p) => p.ticket);
    }
    const vendas: VendasPorMes = data.vendasPorMes;
    return data.monthly.map((m) => ticketDoMes(m.chave ? vendas[m.chave] : undefined));
  }, [periodo, janela, data.vendasPorMes, data.monthly]);

  return (
    <CardBento>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <Rotulo>Ticket médio</Rotulo>
        <SeletorPeriodo valor={periodo} onChange={onPeriodoChange} compacto />
      </div>
      <ValorGrande>{m(formatCurrency(ticket ?? 0, true))}</ValorGrande>
      <Sparkline serie={serie} />
      {varPct != null ? (
        <Sub tom={varPct >= 0 ? 'bom' : 'ruim'}>
          {m(`${varPct >= 0 ? '+' : '−'}${Math.abs(varPct).toFixed(1).replace('.', ',')}%`)} vs.{' '}
          {rotuloDaJanela(anterior)}
        </Sub>
      ) : (
        <Sub>por contrato no {nomeDoPeriodo(periodo)}</Sub>
      )}
    </CardBento>
  );
};
